import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Users, Plus, Pencil, ShieldCheck, Settings2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  mobile: string | null;
  avatar_url: string | null;
  enabled: boolean;
  admin: boolean;
  viewer: boolean;
  pii_enabled?: boolean;
  hourly_unmask_limit?: number;
  download_allowed?: boolean;
  daily_download_limit?: number;
  created_at: string;
}

interface GroupInfo {
  id: string;
  name: string;
}

interface CustomFieldDef {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  is_required: boolean;
  sort_order: number;
}

const UserManagement = () => {
  const { profile: currentProfile } = useAuth();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  // Group data
  const [allGroups, setAllGroups] = useState<GroupInfo[]>([]);
  const [userGroups, setUserGroups] = useState<Record<string, GroupInfo[]>>({});

  // Group filter
  const [filterGroupId, setFilterGroupId] = useState<string>("all");

  // Add profile state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addMobile, setAddMobile] = useState("");
  const [addRole, setAddRole] = useState<"user" | "viewer">("user");
  const [addSaving, setAddSaving] = useState(false);

  // Edit profile state
  const [editOpen, setEditOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editSelectedGroupIds, setEditSelectedGroupIds] = useState<Set<string>>(new Set());
  const [editPiiEnabled, setEditPiiEnabled] = useState(false);
  const [editHourlyUnmaskLimit, setEditHourlyUnmaskLimit] = useState(0);
  const [editDownloadAllowed, setEditDownloadAllowed] = useState(true);
  const [editDailyDownloadLimit, setEditDailyDownloadLimit] = useState(100000);
  const [editSaving, setEditSaving] = useState(false);

  // Custom fields
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDef[]>([]);
  const [editCustomValues, setEditCustomValues] = useState<Record<string, string>>({});

  const fetchCustomFieldDefs = async () => {
    if (!currentProfile?.client_id) return;
    const { data } = await (supabase
      .from("user_custom_field_definitions" as any)
      .select("id, field_name, field_label, field_type, is_required, sort_order")
      .eq("client_id", currentProfile.client_id)
      .order("sort_order", { ascending: true }) as any);
    setCustomFieldDefs(data ?? []);
  };

  const fetchCustomFieldValues = async (profileId: string) => {
    if (customFieldDefs.length === 0) return {};
    const defIds = customFieldDefs.map((d) => d.id);
    const { data } = await (supabase
      .from("user_custom_field_values" as any)
      .select("field_definition_id, field_value")
      .eq("profile_id", profileId)
      .in("field_definition_id", defIds) as any);
    const map: Record<string, string> = {};
    for (const row of (data ?? []) as any[]) {
      map[row.field_definition_id] = row.field_value ?? "";
    }
    return map;
  };

  const fetchProfiles = async () => {
    if (!currentProfile?.client_id) {
      setLoading(false);
      return;
    }

    const { data, error } = await (supabase
      .from("profiles")
      .select("id, full_name, email, mobile, avatar_url, enabled, admin, viewer, pii_enabled, hourly_unmask_limit, download_allowed, daily_download_limit, created_at")
      .eq("client_id", currentProfile.client_id)
      .order("created_at", { ascending: false }) as any);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles(data ?? []);
    }
    setLoading(false);
  };

  const fetchGroups = async () => {
    if (!currentProfile?.client_id) return;

    // Fetch all groups for this client
    const { data: groupData } = await supabase
      .from("groups" as any)
      .select("id, name")
      .eq("client_id", currentProfile.client_id)
      .order("name");

    const groups = (groupData || []) as any[];
    setAllGroups(groups.map((g: any) => ({ id: g.id, name: g.name })));

    // Fetch all group memberships
    if (groups.length > 0) {
      const groupIds = groups.map((g: any) => g.id);
      const { data: memberData } = await supabase
        .from("group_members" as any)
        .select("profile_id, group_id")
        .in("group_id", groupIds);

      const groupMap: Record<string, string> = {};
      groups.forEach((g: any) => { groupMap[g.id] = g.name; });

      const userGroupMap: Record<string, GroupInfo[]> = {};
      if (memberData) {
        (memberData as any[]).forEach((m: any) => {
          if (!userGroupMap[m.profile_id]) {
            userGroupMap[m.profile_id] = [];
          }
          userGroupMap[m.profile_id].push({
            id: m.group_id,
            name: groupMap[m.group_id],
          });
        });
      }
      setUserGroups(userGroupMap);
    } else {
      setUserGroups({});
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchProfiles();
      await fetchGroups();
      await fetchCustomFieldDefs();
    };
    loadData();
  }, [currentProfile?.client_id]);

  const toggleEnabled = async (profile: Profile) => {
    setUpdating(profile.id);
    const { error } = await supabase
      .from("profiles")
      .update({ enabled: !profile.enabled })
      .eq("id", profile.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles((prev) => prev.map((p) => (p.id === profile.id ? { ...p, enabled: !p.enabled } : p)));
    }
    setUpdating(null);
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const resetAddForm = () => {
    setAddName("");
    setAddEmail("");
    setAddMobile("");
    setAddRole("user");
  };

  const handleAddProfile = async () => {
    if (!addName.trim() || !addEmail.trim()) {
      toast({ title: "Error", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    setAddSaving(true);
    try {
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-profile`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            email: addEmail.trim(),
            full_name: addName.trim(),
            mobile: addMobile.trim() || null,
            client_id: currentProfile?.client_id,
            role: addRole,
          }),
        }
      );
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || "Failed to create profile");
      }
      toast({ title: "User created", description: `${addName.trim()} has been added and can now chat on WhatsApp.` });
      setAddOpen(false);
      resetAddForm();
      await fetchProfiles();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setAddSaving(false);
  };

  // Edit handlers
  const openEditDialog = async (profile: Profile) => {
    setEditingProfile(profile);
    setEditName(profile.full_name || "");
    setEditMobile(profile.mobile || "");
    setEditPiiEnabled(profile.pii_enabled ?? false);
    setEditHourlyUnmaskLimit(profile.hourly_unmask_limit ?? 0);
    setEditDownloadAllowed(profile.download_allowed ?? true);
    setEditDailyDownloadLimit(profile.daily_download_limit ?? 100000);
    setEditSelectedGroupIds(new Set((userGroups[profile.id] || []).map((g) => g.id)));
    const cfValues = await fetchCustomFieldValues(profile.id);
    setEditCustomValues(cfValues);
    setEditOpen(true);
  };

  const resetEditForm = () => {
    setEditingProfile(null);
    setEditName("");
    setEditMobile("");
    setEditPiiEnabled(false);
    setEditHourlyUnmaskLimit(0);
    setEditDownloadAllowed(true);
    setEditDailyDownloadLimit(100000);
    setEditSelectedGroupIds(new Set());
    setEditCustomValues({});
  };

  const toggleEditGroup = (groupId: string) => {
    setEditSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleEditProfile = async () => {
    if (!editingProfile) return;
    if (!editName.trim()) {
      toast({ title: "Error", description: "Name is required.", variant: "destructive" });
      return;
    }
    setEditSaving(true);
    try {
      // Update profile fields
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: editName.trim(),
          mobile: editMobile.trim() || null,
          pii_enabled: editPiiEnabled,
          hourly_unmask_limit: editHourlyUnmaskLimit,
          download_allowed: editDownloadAllowed,
          daily_download_limit: editDailyDownloadLimit,
        })
        .eq("id", editingProfile.id);

      if (profileError) throw profileError;

      // Update group memberships - delete existing and insert new
      // First get all group IDs for this client
      const clientGroupIds = allGroups.map((g) => g.id);
      if (clientGroupIds.length > 0) {
        await supabase
          .from("group_members" as any)
          .delete()
          .eq("profile_id", editingProfile.id)
          .in("group_id", clientGroupIds);
      }

      if (editSelectedGroupIds.size > 0) {
        const rows = Array.from(editSelectedGroupIds).map((groupId) => ({
          group_id: groupId,
          profile_id: editingProfile.id,
        }));
        const { error: memberError } = await supabase
          .from("group_members" as any)
          .insert(rows);
        if (memberError) throw memberError;
      }

      // Save custom field values
      for (const def of customFieldDefs) {
        const val = editCustomValues[def.id];
        if (val !== undefined && val !== "") {
          const { data: existing } = await (supabase
            .from("user_custom_field_values" as any)
            .select("id")
            .eq("profile_id", editingProfile.id)
            .eq("field_definition_id", def.id)
            .maybeSingle() as any);
          if (existing) {
            await (supabase
              .from("user_custom_field_values" as any)
              .update({ field_value: val, updated_at: new Date().toISOString() })
              .eq("id", existing.id) as any);
          } else {
            await (supabase
              .from("user_custom_field_values" as any)
              .insert({
                profile_id: editingProfile.id,
                field_definition_id: def.id,
                field_value: val,
              }) as any);
          }
        } else {
          // Delete empty values
          await (supabase
            .from("user_custom_field_values" as any)
            .delete()
            .eq("profile_id", editingProfile.id)
            .eq("field_definition_id", def.id) as any);
        }
      }

      toast({ title: "Updated", description: `${editName.trim()} has been updated.` });
      setEditOpen(false);
      resetEditForm();
      await fetchProfiles();
      await fetchGroups();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setEditSaving(false);
  };

  // Filtered profiles
  const filteredProfiles = filterGroupId === "all"
    ? profiles
    : profiles.filter((p) => {
        const groups = userGroups[p.id] || [];
        return groups.some((g) => g.id === filterGroupId);
      });

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentProfile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <Users className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">User Management</h1>
          <p className="text-sm text-muted-foreground">
            Manage users in your organization — enable or disable access.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Group filter */}
      {allGroups.length > 0 && (
        <div className="flex items-center gap-3">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">Filter by Group:</Label>
          <Select value={filterGroupId} onValueChange={setFilterGroupId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {allGroups.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterGroupId !== "all" && (
            <span className="text-xs text-muted-foreground">
              {filteredProfiles.length} user{filteredProfiles.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>
      )}

      {profiles.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No users yet</CardTitle>
          <CardDescription>Users will appear here once they sign up.</CardDescription>
        </Card>
      ) : filteredProfiles.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No users in this group</CardTitle>
          <CardDescription>No users are assigned to the selected group.</CardDescription>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-center">PII Access</TableHead>
                <TableHead className="text-center">Download</TableHead>
                <TableHead className="text-center">Daily Limit</TableHead>
                <TableHead className="text-center">Enabled</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProfiles.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={p.avatar_url ?? undefined} />
                        <AvatarFallback className="text-xs">
                          {getInitials(p.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">
                        {p.full_name || <span className="text-muted-foreground italic">Unnamed</span>}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.email || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.mobile || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.admin ? "Admin" : p.viewer ? "Viewer" : "User"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(userGroups[p.id] || []).length > 0 ? (
                        (userGroups[p.id] || []).map((g) => (
                          <Badge key={g.id} variant="secondary" className="text-xs">
                            {g.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground italic">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={p.pii_enabled ? "default" : "secondary"} className="text-[10px]">
                      {p.pii_enabled ? "Full" : "Masked"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={(p.download_allowed ?? true) ? "default" : "secondary"} className="text-[10px]">
                      {(p.download_allowed ?? true) ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-xs text-muted-foreground">
                    {(p.daily_download_limit ?? 100000).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={p.enabled}
                      onCheckedChange={() => toggleEnabled(p)}
                      disabled={updating === p.id || p.id === currentProfile.id}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(p)}
                      title="Edit user"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { if (!open) { setAddOpen(false); resetAddForm(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="add-name">Full Name *</Label>
              <Input id="add-name" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Full name" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-email">Email *</Label>
              <Input id="add-email" type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="email@example.com" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-mobile">Mobile (WhatsApp number)</Label>
              <Input id="add-mobile" type="tel" value={addMobile} onChange={(e) => setAddMobile(e.target.value)} placeholder="e.g. 919876543210" />
            </div>
            <div className="grid gap-1.5">
              <Label>Role</Label>
              <Select value={addRole} onValueChange={(v) => setAddRole(v as "user" | "viewer")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User (full access)</SelectItem>
                  <SelectItem value="viewer">Viewer (read-only reports & dashboards)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); resetAddForm(); }} disabled={addSaving}>
              Cancel
            </Button>
            <Button onClick={handleAddProfile} disabled={addSaving}>
              {addSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => { if (!open) { setEditOpen(false); resetEditForm(); } }}>
        <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and group assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 flex-1 overflow-y-auto">
            <div className="grid gap-1.5">
              <Label htmlFor="edit-name">Full Name *</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edit-mobile">Mobile (WhatsApp number)</Label>
              <Input
                id="edit-mobile"
                type="tel"
                value={editMobile}
                onChange={(e) => setEditMobile(e.target.value)}
                placeholder="e.g. 919876543210"
              />
            </div>
            {allGroups.length > 0 && (
              <div className="grid gap-1.5">
                <Label>Assign Groups</Label>
                <div className="text-xs text-muted-foreground mb-1">
                  {editSelectedGroupIds.size} group{editSelectedGroupIds.size !== 1 ? "s" : ""} selected
                </div>
                <div className="border rounded-md max-h-[200px] overflow-y-auto">
                  {allGroups.map((g) => (
                    <label
                      key={g.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer border-b last:border-0"
                    >
                      <Checkbox
                        checked={editSelectedGroupIds.has(g.id)}
                        onCheckedChange={() => toggleEditGroup(g.id)}
                      />
                      <span className="text-sm">{g.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="edit-pii" className="text-sm font-medium">PII Access</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {editPiiEnabled
                    ? "User can see all PII data without masking."
                    : "PII data will be masked across chat, reports, and dashboards."}
                </p>
              </div>
              <Switch
                id="edit-pii"
                checked={editPiiEnabled}
                onCheckedChange={setEditPiiEnabled}
              />
            </div>
            {!editPiiEnabled && (
              <div className="grid gap-1.5">
                <Label htmlFor="edit-unmask-limit">Hourly Unmask Limit</Label>
                <Input
                  id="edit-unmask-limit"
                  type="number"
                  min={0}
                  value={editHourlyUnmaskLimit}
                  onChange={(e) => setEditHourlyUnmaskLimit(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of PII unmask actions allowed per hour. Set to 0 to disallow unmasking.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="edit-download" className="text-sm font-medium">Download Access</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {editDownloadAllowed
                    ? "User can download data from reports, queries, and dashboards."
                    : "Download buttons are hidden for this user."}
                </p>
              </div>
              <Switch
                id="edit-download"
                checked={editDownloadAllowed}
                onCheckedChange={setEditDownloadAllowed}
              />
            </div>
            {editDownloadAllowed && (
              <div className="grid gap-1.5">
                <Label htmlFor="edit-daily-limit">Daily Download Limit (rows)</Label>
                <Input
                  id="edit-daily-limit"
                  type="number"
                  min={0}
                  value={editDailyDownloadLimit}
                  onChange={(e) => setEditDailyDownloadLimit(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="100000"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of rows this user can download per day across all reports and queries.
                </p>
              </div>
            )}
            {customFieldDefs.length > 0 && (
              <div className="grid gap-3">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Custom Fields</Label>
                </div>
                {customFieldDefs.map((def) => (
                  <div key={def.id} className="grid gap-1.5">
                    <Label htmlFor={`cf-${def.id}`} className="text-sm">
                      {def.field_label}
                      {def.is_required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {def.field_type === "boolean" ? (
                      <Select
                        value={editCustomValues[def.id] || ""}
                        onValueChange={(v) => setEditCustomValues((prev) => ({ ...prev, [def.id]: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">—</SelectItem>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : def.field_type === "profile_link" ? (
                      <Select
                        value={editCustomValues[def.id] || ""}
                        onValueChange={(v) => setEditCustomValues((prev) => ({ ...prev, [def.id]: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select user..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">—</SelectItem>
                          {profiles
                            .filter((pp) => pp.id !== editingProfile?.id)
                            .map((pp) => (
                              <SelectItem key={pp.id} value={pp.id}>
                                {pp.full_name || pp.email || pp.id}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : def.field_type === "date" ? (
                      <Input
                        id={`cf-${def.id}`}
                        type="date"
                        value={editCustomValues[def.id] || ""}
                        onChange={(e) => setEditCustomValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                      />
                    ) : def.field_type === "number" ? (
                      <Input
                        id={`cf-${def.id}`}
                        type="number"
                        value={editCustomValues[def.id] || ""}
                        onChange={(e) => setEditCustomValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                        placeholder="0"
                      />
                    ) : (
                      <Input
                        id={`cf-${def.id}`}
                        type="text"
                        value={editCustomValues[def.id] || ""}
                        onChange={(e) => setEditCustomValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                        placeholder={`Enter ${def.field_label.toLowerCase()}...`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditOpen(false); resetEditForm(); }} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleEditProfile} disabled={editSaving}>
              {editSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
